import type { GridCell, GridSelection } from "@glideapps/glide-data-grid";
import { getCellKind } from "@/payroll/ai-script/cell-kind-utils";
import type {
  AiSpreadsheetCellKind,
  AiSpreadsheetValueType,
  IAiSpreadsheetCell,
  IAiSpreadsheetCellFormat,
} from "@/payroll/ai-script/types";
import {
  AI_SPREADSHEET_CELL_KIND,
  AI_SPREADSHEET_VALUE_TYPE,
} from "@/payroll/ai-script/types";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";
import { serializeTabSeparatedClipboard } from "./clipboard-text";
import { convertFormulaToR1C1 } from "./formula-reference-utils";
import {
  buildGoogleSheetsCompactTable,
  buildGoogleSheetsValue,
} from "./google-sheets-clipboard";

export interface WorkbookCopiedCell {
  computedValue?: string | number | boolean | null;
  format?: IAiSpreadsheetCellFormat | null;
  kind: AiSpreadsheetCellKind;
  rawInput: string;
  /** Saved workbook coordinates. Older clipboard payloads can omit them. */
  sourceRow?: number;
  sourceCol?: number;
  valueType?: AiSpreadsheetValueType;
}

export interface WorkbookCopiedRange {
  // 1-based source coordinate of the copied rectangle's top-left cell.
  startRow: number;
  startCol: number;
  // The values placed in text/plain and text/html. These are used to prove the
  // clipboard still contains this copy before private metadata is trusted.
  values: readonly (readonly string[])[];
  // Volca-only source data, aligned to values. The HTML serializer stores the
  // formula/type fields in ignorable data-* attributes for internal paste.
  cells?: readonly (readonly (WorkbookCopiedCell | null)[])[];
}

export interface WorkbookClipboardPayload {
  googleSheetsCompactTable: string;
  textHtml: string;
  textPlain: string;
}

export const GOOGLE_SHEETS_COMPACT_TABLE_MIME =
  "application/x-vnd.google-spreadsheet-compact-table+json";

const asValueType = (
  value: string | null | undefined,
): AiSpreadsheetValueType | undefined =>
  value != null &&
  Object.values(AI_SPREADSHEET_VALUE_TYPE).includes(
    value as AiSpreadsheetValueType,
  )
    ? (value as AiSpreadsheetValueType)
    : undefined;

const escapeHtml = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const escapeHtmlAttribute = (value: string) =>
  escapeHtml(value).replace(/"/g, "&quot;");

const serializeComputedValue = (value: WorkbookCopiedCell["computedValue"]) =>
  value === undefined ? undefined : JSON.stringify(value);

const parseComputedValue = (
  value: string | undefined,
): WorkbookCopiedCell["computedValue"] => {
  if (value === undefined) return undefined;

  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === "string" ||
      typeof parsed === "number" ||
      typeof parsed === "boolean" ||
      parsed === null
      ? parsed
      : undefined;
  } catch {
    return undefined;
  }
};

const clipboardColor = (value: string | null | undefined) => {
  if (!value) return null;
  const hex = value.replace(/^#/, "");
  if (!/^(?:[\da-f]{6}|[\da-f]{8})$/i.test(hex)) return null;
  return `#${hex.slice(-6).toLowerCase()}`;
};

const buildClipboardCellStyle = (cell: WorkbookCopiedCell | null) => {
  if (!cell) return "";

  const styles: string[] = [];
  const backgroundColor = clipboardColor(cell.format?.bg_color);
  const foregroundColor = clipboardColor(cell.format?.fg_color);
  const alignment = cell.format?.alignment?.horizontal;

  if (backgroundColor) styles.push(`background-color:${backgroundColor}`);
  if (foregroundColor) styles.push(`color:${foregroundColor}`);
  if (cell.format?.bold) styles.push("font-weight:bold");
  if (alignment === "left" || alignment === "center" || alignment === "right") {
    styles.push(`text-align:${alignment}`);
  }
  if (cell.format?.alignment?.wrap_text) {
    styles.push("white-space:pre-wrap");
  }

  return styles.join(";");
};

// The interoperable clipboard representation is the same pair browsers and
// spreadsheet apps exchange: tab-separated text plus an HTML table. Volca's
// extra formula/source data lives in ignorable data-* attributes on that table,
// so it survives cross-tab paste without changing what receiving apps see.
export const buildWorkbookClipboardPayload = (
  copiedRange: WorkbookCopiedRange,
): WorkbookClipboardPayload => {
  const clipboardValues = copiedRange.values;
  const textPlain = serializeTabSeparatedClipboard(copiedRange.values);
  const rows = copiedRange.values.map((row, rowIndex) => {
    const cells = row.map((value, colIndex) => {
      const copiedCell = copiedRange.cells?.[rowIndex]?.[colIndex] ?? null;
      const clipboardValue = clipboardValues[rowIndex]?.[colIndex] ?? value;
      const sheetsValue = buildGoogleSheetsValue(value, copiedCell);
      const attributes = [
        `gdg-raw-value="${escapeHtmlAttribute(clipboardValue)}"`,
        `data-volca-display-value="${escapeHtmlAttribute(value)}"`,
        `data-sheets-value="${escapeHtmlAttribute(sheetsValue)}"`,
      ];
      const contentAttributes = [
        `data-sheets-value="${escapeHtmlAttribute(sheetsValue)}"`,
      ];
      const style = buildClipboardCellStyle(copiedCell);

      if (copiedCell) {
        attributes.push(`data-volca-kind="${copiedCell.kind}"`);
        attributes.push(
          `data-volca-raw-input="${escapeHtmlAttribute(copiedCell.rawInput)}"`,
        );
        if (copiedCell.sourceRow) {
          attributes.push(`data-volca-source-row="${copiedCell.sourceRow}"`);
        }
        if (copiedCell.sourceCol) {
          attributes.push(`data-volca-source-col="${copiedCell.sourceCol}"`);
        }
        const computedValue = serializeComputedValue(copiedCell.computedValue);
        if (computedValue !== undefined) {
          attributes.push(
            `data-volca-computed-value="${escapeHtmlAttribute(computedValue)}"`,
          );
        }
        if (copiedCell.valueType) {
          attributes.push(`data-volca-value-type="${copiedCell.valueType}"`);
        }
        if (copiedCell.kind === AI_SPREADSHEET_CELL_KIND.formula) {
          const formula = convertFormulaToR1C1(
            copiedCell.rawInput,
            copiedCell.sourceRow ?? copiedRange.startRow + rowIndex,
            copiedCell.sourceCol ?? copiedRange.startCol + colIndex,
          );
          contentAttributes.push(
            `data-sheets-formula="${escapeHtmlAttribute(formula)}"`,
          );
        }
      }
      if (style) attributes.push(`style="${style}"`);

      return `<td ${attributes.join(" ")}><span ${contentAttributes.join(" ")}>${escapeHtml(clipboardValue).replace(/\n/g, "<br>")}</span></td>`;
    });

    return `<tr>${cells.join("")}</tr>`;
  });
  const textHtml = `<table data-sheets-root="1" data-volca-workbook-copy="1" data-volca-start-row="${copiedRange.startRow}" data-volca-start-col="${copiedRange.startCol}"><tbody>${rows.join("")}</tbody></table>`;

  return {
    googleSheetsCompactTable: buildGoogleSheetsCompactTable(copiedRange),
    textHtml,
    textPlain,
  };
};

export const parseWorkbookCopiedRange = (
  html: string,
): WorkbookCopiedRange | null => {
  if (typeof DOMParser === "undefined") return null;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const table = doc.querySelector<HTMLTableElement>(
    'table[data-volca-workbook-copy="1"]',
  );
  if (!table) return null;

  const startRow = Number(table.dataset.volcaStartRow);
  const startCol = Number(table.dataset.volcaStartCol);
  if (
    !Number.isInteger(startRow) ||
    startRow < 1 ||
    !Number.isInteger(startCol) ||
    startCol < 1
  ) {
    return null;
  }

  const values: string[][] = [];
  const cells: (WorkbookCopiedCell | null)[][] = [];

  Array.from(table.rows).forEach((row) => {
    const valueRow: string[] = [];
    const cellRow: (WorkbookCopiedCell | null)[] = [];

    Array.from(row.cells).forEach((cell) => {
      valueRow.push(cell.dataset.volcaDisplayValue ?? cell.textContent ?? "");
      const kind = cell.dataset.volcaKind;
      const valueType = cell.dataset.volcaValueType;
      const sourceRow = Number(cell.dataset.volcaSourceRow);
      const sourceCol = Number(cell.dataset.volcaSourceCol);
      if (kind !== "formula" && kind !== "value") {
        cellRow.push(null);
        return;
      }

      cellRow.push({
        computedValue: parseComputedValue(cell.dataset.volcaComputedValue),
        kind,
        rawInput: cell.dataset.volcaRawInput ?? "",
        sourceRow:
          Number.isInteger(sourceRow) && sourceRow > 0 ? sourceRow : undefined,
        sourceCol:
          Number.isInteger(sourceCol) && sourceCol > 0 ? sourceCol : undefined,
        valueType: asValueType(valueType),
      });
    });

    values.push(valueRow);
    cells.push(cellRow);
  });

  return { cells, startCol, startRow, values };
};

interface GridCopyRange {
  x: number;
  y: number;
  width: number;
  height: number;
}

const contiguousSelectionBounds = (indexes: number[]) => {
  if (indexes.length === 0) return null;

  const sorted = [...indexes].sort((a, b) => a - b);
  const isContiguous = sorted.every(
    (value, index) => index === 0 || value === sorted[index - 1] + 1,
  );
  return isContiguous ? { start: sorted[0], length: sorted.length } : null;
};

// Glide represents row-header and column-header selections outside
// `selection.current`. Resolve those selections to the rectangle that Glide
// copies so formula pastes retain their workbook source coordinates.
export const getGridCopyRange = (
  selection: GridSelection,
  gridRowCount: number,
  gridColumnCount: number,
): GridCopyRange | null => {
  const selectedRows = contiguousSelectionBounds(selection.rows.toArray());
  if (selectedRows && gridColumnCount > 0) {
    return {
      x: 0,
      y: selectedRows.start,
      width: gridColumnCount,
      height: selectedRows.length,
    };
  }

  const selectedColumns = contiguousSelectionBounds(
    selection.columns.toArray(),
  );
  if (selectedColumns && gridRowCount > 0) {
    return {
      x: selectedColumns.start,
      y: 0,
      width: selectedColumns.length,
      height: gridRowCount,
    };
  }

  return selection.current?.range ?? null;
};

interface BuildWorkbookCopiedRangeParams {
  range: GridCopyRange;
  cells: readonly (readonly GridCell[])[];
  getCellRecord: (
    colIndex: number,
    rowIndex: number,
  ) => IAiSpreadsheetCell | undefined;
}

// Shape grid cells and their backing workbook records once at the copy boundary,
// keeping clipboard-specific data assembly out of the viewer component.
export const buildWorkbookCopiedRange = ({
  range,
  cells,
  getCellRecord,
}: BuildWorkbookCopiedRangeParams): WorkbookCopiedRange => {
  const records = cells.map((row, rowOffset) =>
    row.map((_cell, colOffset) =>
      getCellRecord(range.x + colOffset, range.y + rowOffset),
    ),
  );

  return {
    startRow: range.y + 1,
    startCol: range.x + 1,
    values: cells.map((row, rowOffset) =>
      row.map((cell, colOffset) => {
        const record = records[rowOffset]?.[colOffset];
        return record
          ? getAiSpreadsheetCellDisplayValue(record)
          : String(cell.copyData ?? "");
      }),
    ),
    cells: records.map((row) =>
      row.map((record) =>
        record
          ? {
              computedValue: record.computed_value,
              format: record.format,
              kind: getCellKind(record),
              rawInput: record.raw_input ?? "",
              sourceRow: record.row,
              sourceCol: record.col,
              valueType: asValueType(record.value_type),
            }
          : null,
      ),
    ),
  };
};
